import {
  type SettingsState as SettingsStoreBase,
  settingsStateCreator,
} from "@nijiurachan/js/io/settings-store";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";
import { createStore, type StoreApi } from "zustand/vanilla";
import {
  type LegacyDisplaySettings,
  readLegacyDisplaySettings,
} from "@/features/settings/stores/legacyDisplaySettings";
import {
  getSafeLocalStorage,
  type SafeStorage,
} from "@/features/settings/stores/safeStorage";

/** 全体文字倍率の最小値(%) */
export const GLOBAL_FONT_SCALE_MIN = 50;
/** 全体文字倍率の最大値(%) */
export const GLOBAL_FONT_SCALE_MAX = 175;
/** 全体文字倍率の既定値(%) */
export const GLOBAL_FONT_SCALE_DEFAULT = 100;

/** スレッド・レス本文の最小文字サイズ(px) */
export const POST_BODY_FONT_SIZE_MIN = 8;
/** スレッド・レス本文の最大文字サイズ(px) */
export const POST_BODY_FONT_SIZE_MAX = 28;
/** スレッド・レス本文の既定文字サイズ(px) */
export const POST_BODY_FONT_SIZE_DEFAULT = 16;

const SETTINGS_PERSIST_VERSION = 4;

export const THREAD_IMAGE_SCALE_OPTIONS = [60, 80, 100] as const;
export type ThreadImageScale = (typeof THREAD_IMAGE_SCALE_OPTIONS)[number];
export const THREAD_IMAGE_SCALE_DEFAULT: ThreadImageScale = 100;

/** 表示密度。"compact"はPC版の余白を詰める */
export type DisplayDensity = "comfortable" | "compact";

export type NewRepliesBannerWidth = "default" | "compact" | "full";
export type NewRepliesBannerPosition =
  | "default"
  | "left"
  | "center-left"
  | "center"
  | "center-right"
  | "right";

/**
 * font-family値として安全な文字だけを残す。CSSカスタムプロパティへ流し込むため、
 * 宣言やブロックを終端しうる記号(; { } ( ) < > \ など)と改行を落とす。
 * 空文字(既定)なら本体既定のフォントを使う。
 */
export function sanitizeFontFamily(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .replace(/[;{}()<>[\]\\@:!/*]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);
}

/** 表示密度を有効な値に丸める。列挙値以外(壊れた永続値等)は既定(comfortable)にフォールバック */
export function sanitizeDisplayDensity(value: unknown): DisplayDensity {
  return value === "compact" ? "compact" : "comfortable";
}

export function sanitizeNewRepliesBannerWidth(
  value: unknown,
): NewRepliesBannerWidth {
  return value === "compact" || value === "full" ? value : "default";
}

export function sanitizeNewRepliesBannerPosition(
  value: unknown,
): NewRepliesBannerPosition {
  return value === "left" ||
    value === "center-left" ||
    value === "center" ||
    value === "center-right" ||
    value === "right"
    ? value
    : "default";
}

/** PC版スレッドでインライン拡大する画像の倍率を有効な選択肢へ丸める */
export function sanitizeThreadImageScale(value: unknown): ThreadImageScale {
  return THREAD_IMAGE_SCALE_OPTIONS.includes(value as ThreadImageScale)
    ? (value as ThreadImageScale)
    : THREAD_IMAGE_SCALE_DEFAULT;
}

/** 数値欄を有効な範囲に丸める。異常値は初期値にフォールバック */
function clampSize(
  value: unknown,
  min: number,
  max: number,
  defaultValue: number,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return defaultValue;
  }
  return Math.max(min, Math.min(max, Math.round(value)));
}

/** 全体文字倍率を有効な範囲に丸める。異常値は初期値にフォールバック */
export function clampGlobalFontScale(value: unknown): number {
  return clampSize(
    value,
    GLOBAL_FONT_SCALE_MIN,
    GLOBAL_FONT_SCALE_MAX,
    GLOBAL_FONT_SCALE_DEFAULT,
  );
}

/** 本文文字サイズを有効な範囲に丸める。異常値は初期値にフォールバック */
export function clampPostBodyFontSize(value: unknown): number {
  return clampSize(
    value,
    POST_BODY_FONT_SIZE_MIN,
    POST_BODY_FONT_SIZE_MAX,
    POST_BODY_FONT_SIZE_DEFAULT,
  );
}

/** 表示設定のAPI */
export type SettingsStore = SettingsStoreBase & {
  /** 全体文字倍率(%) */
  globalFontScale: number;
  /** 全体文字倍率を設定 */
  setGlobalFontScale(value: number): void;
  /** スレッド・レス本文の文字サイズ(px) */
  postBodyFontSize: number;
  /** スレッド・レス本文の文字サイズを設定 */
  setPostBodyFontSize(value: number): void;
  /** 宇宙モード（映画オープニング風クロール表示）を有効にする */
  spaceMode: boolean;
  /** 宇宙モードを設定 */
  setSpaceMode(value: boolean): void;
  /** ジュークボックス（共有音楽プレイヤー）を有効にする */
  jukeboxEnabled: boolean;
  /** ジュークボックスの有効/無効を設定 */
  setJukeboxEnabled(value: boolean): void;
  /** R18添付を常時表示する */
  showR18: boolean;
  /** R18添付の表示方法をユーザーが選択済みか */
  hasSelectedR18Preference: boolean;
  setShowR18(value: boolean): void;
  /** 未選択の初回来訪者だけR18添付のクッション表示を有効にする */
  applyInitialR18Cushion(): void;
  /** 現在のR18添付表示設定をユーザーの選択として確定する */
  confirmR18Preference(): void;
  ogpPreviewEnabled: boolean;
  setOgpPreviewEnabled(value: boolean): void;
  /** 外部埋め込み（YouTube等のiframe）を表示する。既定OFF（リンクのみ表示、Issue #49） */
  externalEmbedEnabled: boolean;
  setExternalEmbedEnabled(value: boolean): void;
  /** 添付画像クリック時に画像ビューアーを使わず別タブで開く */
  imageClickOpensNewTab: boolean;
  setImageClickOpensNewTab(value: boolean): void;
  /** AI画像メタデータの読み取り・表示を有効にする */
  aiImageMetadataEnabled: boolean;
  setAiImageMetadataEnabled(value: boolean): void;
  /** スレッドのAI要約を表示する */
  threadAiSummaryEnabled: boolean;
  setThreadAiSummaryEnabled(value: boolean): void;
  /** レス本文のフォント。空文字なら本体既定のまま */
  fontFamily: string;
  setFontFamily(value: string): void;
  /** PC版の表示密度 */
  displayDensity: DisplayDensity;
  setDisplayDensity(value: DisplayDensity): void;
  /** PC版スレッドでインライン拡大する画像の倍率 */
  threadImageScale: ThreadImageScale;
  setThreadImageScale(value: ThreadImageScale): void;
  /** PC版の共通ヘッダーとページツールバーを画面上部へ固定する */
  headerFixed: boolean;
  setHeaderFixed(value: boolean): void;
  /** 定型文の発光対象を引用行・カタログ・追加語句へ広げる */
  extendedTextEffects: boolean;
  setExtendedTextEffects(value: boolean): void;
  /** 画面端でのホイール操作による更新を許可する */
  wheelReloadEnabled: boolean;
  setWheelReloadEnabled(value: boolean): void;
  /** スレッドを一定間隔で自動更新する */
  threadAutoReload: boolean;
  setThreadAutoReload(value: boolean): void;
  /** PC版のレス送信ボタンを画面右下へ固定する */
  replyButtonFixed: boolean;
  setReplyButtonFixed(value: boolean): void;
  /** PC版のレス送信パネルを簡易表示にする */
  desktopReplyPanelSimplified: boolean;
  setDesktopReplyPanelSimplified(value: boolean): void;
  /** PC版のレス送信パネルをピン留めし、送信後も開いたままにする */
  desktopReplyPanelPinned: boolean;
  setDesktopReplyPanelPinned(value: boolean): void;
  /** 新着レス通知の横幅 */
  newRepliesBannerWidth: NewRepliesBannerWidth;
  setNewRepliesBannerWidth(value: NewRepliesBannerWidth): void;
  /** 新着レス通知の横位置 */
  newRepliesBannerPosition: NewRepliesBannerPosition;
  setNewRepliesBannerPosition(value: NewRepliesBannerPosition): void;
};

/** 廃止済み設定を除外して永続値を現在の設定へ統合する */
function mergePersistedSettings(
  persistedState: unknown,
  currentState: SettingsStore,
): SettingsStore {
  const mergedState =
    typeof persistedState === "object" && persistedState !== null
      ? Object.assign({}, currentState, persistedState)
      : currentState;
  Reflect.deleteProperty(mergedState, "ctrlEnterSubmit");
  Reflect.deleteProperty(mergedState, "fontScalePosts");
  Reflect.deleteProperty(mergedState, "fontSize");
  Reflect.deleteProperty(mergedState, "unindentQuotePosts");
  Reflect.deleteProperty(mergedState, "dragQuotePencilSize");
  Reflect.deleteProperty(mergedState, "enhancedQuotePreview");
  Reflect.deleteProperty(mergedState, "bodyMaxWidth");
  return mergedState;
}

/** 旧表示設定を現在の単位・プロパティへ移行する */
export function migratePersistedSettings(
  persistedState: unknown,
  version: number,
): unknown {
  if (version > SETTINGS_PERSIST_VERSION) {
    throw new Error(`Unsupported settings version: ${version}`);
  }

  if (typeof persistedState !== "object" || persistedState === null) {
    return persistedState;
  }

  const migratedState: Record<string, unknown> = { ...persistedState };
  const hasPersistedR18Preference = typeof migratedState.showR18 === "boolean";
  if (version < 1) {
    migratedState.postBodyFontSize = POST_BODY_FONT_SIZE_DEFAULT;
  }
  if (version < 2) {
    migratedState.globalFontScale = GLOBAL_FONT_SCALE_DEFAULT;
  }
  if (version < 3) {
    // 保存値がない利用者だけ、R18添付ぼかしの新しい既定へ移行する。
    // booleanの保存値はユーザーが選んだtrue/falseとしてそのまま維持する。
    if (typeof migratedState.showR18 !== "boolean") {
      migratedState.showR18 = true;
    }
  }
  if (version < 4) {
    migratedState.hasSelectedR18Preference = hasPersistedR18Preference;
  }
  Reflect.deleteProperty(migratedState, "fontScalePosts");
  Reflect.deleteProperty(migratedState, "fontSize");
  Reflect.deleteProperty(migratedState, "unindentQuotePosts");
  Reflect.deleteProperty(migratedState, "dragQuotePencilSize");
  Reflect.deleteProperty(migratedState, "enhancedQuotePreview");
  Reflect.deleteProperty(migratedState, "bodyMaxWidth");
  return migratedState;
}

/** 表示設定のストアを作る */
export const createSettingsStore: () => StoreApi<SettingsStore> = () => {
  const safeStorage = getSafeLocalStorage();
  let getCurrentSettings: (() => SettingsStore) | undefined;
  // persist初期化がnijiurachan-settingsを作る前に、旧設定の移行可否を確定する。
  // キーが壊れていても「存在する」なら新設定を常に優先する。
  const legacyDisplaySettings =
    safeStorage === undefined ? null : readLegacyDisplaySettings(safeStorage);

  const persistStorage: StateStorage = safeStorage ?? {
    getItem: () => null,
    setItem: () => undefined,
    removeItem: () => undefined,
  };

  return createStore<SettingsStore>()(
    persist(
      (...args) => {
        const [set, get] = args;
        getCurrentSettings = get;
        const base = settingsStateCreator(...args);
        return {
          ...base,
          // njs側の既定値と同じく、初回表示はOSのテーマ設定へ追従する。
          darkMode: "system",
          globalFontScale: GLOBAL_FONT_SCALE_DEFAULT,
          setGlobalFontScale: (value: number) =>
            set({ globalFontScale: clampGlobalFontScale(value) }),
          postBodyFontSize: POST_BODY_FONT_SIZE_DEFAULT,
          setPostBodyFontSize: (value: number) =>
            set({ postBodyFontSize: clampPostBodyFontSize(value) }),
          spaceMode: false,
          setSpaceMode: (spaceMode: boolean) => set({ spaceMode }),
          jukeboxEnabled: true,
          setJukeboxEnabled: (jukeboxEnabled: boolean) =>
            set({ jukeboxEnabled }),
          showR18: true,
          hasSelectedR18Preference: false,
          setShowR18: (showR18: boolean) =>
            set({ showR18, hasSelectedR18Preference: true }),
          applyInitialR18Cushion: () => {
            if (!get().hasSelectedR18Preference) {
              set({ showR18: false });
            }
          },
          confirmR18Preference: () => set({ hasSelectedR18Preference: true }),
          ogpPreviewEnabled: true,
          setOgpPreviewEnabled: (ogpPreviewEnabled: boolean) =>
            set({ ogpPreviewEnabled }),
          externalEmbedEnabled: false,
          setExternalEmbedEnabled: (externalEmbedEnabled: boolean) =>
            set({ externalEmbedEnabled }),
          imageClickOpensNewTab: false,
          setImageClickOpensNewTab: (imageClickOpensNewTab: boolean) =>
            set({ imageClickOpensNewTab }),
          aiImageMetadataEnabled: true,
          setAiImageMetadataEnabled: (aiImageMetadataEnabled: boolean) =>
            set({ aiImageMetadataEnabled }),
          threadAiSummaryEnabled: true,
          setThreadAiSummaryEnabled: (threadAiSummaryEnabled: boolean) =>
            set({ threadAiSummaryEnabled }),
          fontFamily: "",
          setFontFamily: (value: string) =>
            set({ fontFamily: sanitizeFontFamily(value) }),
          displayDensity: "comfortable",
          setDisplayDensity: (displayDensity: DisplayDensity) =>
            set({ displayDensity: sanitizeDisplayDensity(displayDensity) }),
          threadImageScale: THREAD_IMAGE_SCALE_DEFAULT,
          setThreadImageScale: (threadImageScale: ThreadImageScale) =>
            set({
              threadImageScale: sanitizeThreadImageScale(threadImageScale),
            }),
          headerFixed: false,
          setHeaderFixed: (headerFixed: boolean) => set({ headerFixed }),
          extendedTextEffects: false,
          setExtendedTextEffects: (extendedTextEffects: boolean) =>
            set({ extendedTextEffects }),
          wheelReloadEnabled: true,
          setWheelReloadEnabled: (wheelReloadEnabled: boolean) =>
            set({ wheelReloadEnabled }),
          threadAutoReload: false,
          setThreadAutoReload: (threadAutoReload: boolean) =>
            set({ threadAutoReload }),
          replyButtonFixed: true,
          setReplyButtonFixed: (replyButtonFixed: boolean) =>
            set({ replyButtonFixed }),
          desktopReplyPanelSimplified: false,
          setDesktopReplyPanelSimplified: (
            desktopReplyPanelSimplified: boolean,
          ) => set({ desktopReplyPanelSimplified }),
          desktopReplyPanelPinned: false,
          setDesktopReplyPanelPinned: (desktopReplyPanelPinned: boolean) =>
            set({ desktopReplyPanelPinned }),
          newRepliesBannerWidth: "default",
          setNewRepliesBannerWidth: (
            newRepliesBannerWidth: NewRepliesBannerWidth,
          ) =>
            set({
              newRepliesBannerWidth: sanitizeNewRepliesBannerWidth(
                newRepliesBannerWidth,
              ),
            }),
          newRepliesBannerPosition: "default",
          setNewRepliesBannerPosition: (
            newRepliesBannerPosition: NewRepliesBannerPosition,
          ) =>
            set({
              newRepliesBannerPosition: sanitizeNewRepliesBannerPosition(
                newRepliesBannerPosition,
              ),
            }),
          resetSettings(): void {
            base.resetSettings();
            set({
              darkMode: "system",
              globalFontScale: GLOBAL_FONT_SCALE_DEFAULT,
              postBodyFontSize: POST_BODY_FONT_SIZE_DEFAULT,
              spaceMode: false,
              jukeboxEnabled: true,
              showR18: true,
              hasSelectedR18Preference: false,
              ogpPreviewEnabled: true,
              externalEmbedEnabled: false,
              imageClickOpensNewTab: false,
              aiImageMetadataEnabled: true,
              threadAiSummaryEnabled: true,
              fontFamily: "",
              displayDensity: "comfortable",
              threadImageScale: THREAD_IMAGE_SCALE_DEFAULT,
              headerFixed: false,
              extendedTextEffects: false,
              wheelReloadEnabled: true,
              threadAutoReload: false,
              replyButtonFixed: true,
              desktopReplyPanelSimplified: false,
              desktopReplyPanelPinned: false,
              newRepliesBannerWidth: "default",
              newRepliesBannerPosition: "default",
            });
          },
        };
      },
      {
        name: "nijiurachan-settings",
        version: SETTINGS_PERSIST_VERSION,
        storage: createJSONStorage(() => persistStorage),
        migrate: migratePersistedSettings,
        merge: mergePersistedSettings,
        onRehydrateStorage:
          () =>
          (store: SettingsStore | undefined): void => {
            void initSettings(
              store,
              legacyDisplaySettings,
              safeStorage,
              getCurrentSettings,
            );
          },
      },
    ),
  );
};

/** 表示設定の初期化。ストレージ読込み後呼ばれる */
async function initSettings(
  store: SettingsStore | undefined,
  legacyDisplaySettings: LegacyDisplaySettings | null,
  storage: SafeStorage | undefined,
  getCurrentSettings: (() => SettingsStore) | undefined,
): Promise<void> {
  if (!store) {
    return;
  }

  if (legacyDisplaySettings) {
    if (legacyDisplaySettings.darkMode !== undefined) {
      store.setDarkMode(legacyDisplaySettings.darkMode);
    }
    if (legacyDisplaySettings.privacyMode !== undefined) {
      store.setPrivacyMode(legacyDisplaySettings.privacyMode);
    }
  }

  // 永続化・移行された文字サイズが範囲外/壊れていた場合は補正
  store.setGlobalFontScale(store.globalFontScale);
  store.setPostBodyFontSize(store.postBodyFontSize);
  // 手書き・旧バージョンの壊れた永続値でCSSへ異常値が流れないよう正規化する。
  // 各setterが自身で検証・丸めを行うため、ここでは現在値をそのまま渡すだけでよい
  store.setFontFamily(store.fontFamily);
  store.setDisplayDensity(store.displayDensity);
  store.setThreadImageScale(store.threadImageScale);
  store.setNewRepliesBannerWidth(store.newRepliesBannerWidth);
  store.setNewRepliesBannerPosition(store.newRepliesBannerPosition);

  if (
    storage !== undefined &&
    storage.getItem("aimg_legacy_thread_settings_migrated") !== "1"
  ) {
    const legacyOgp = storage.getItem("futaba_ogp_preview");
    if (legacyOgp !== null) store.setOgpPreviewEnabled(legacyOgp !== "0");
    storage.setItem("aimg_legacy_thread_settings_migrated", "1");
  }

  if (store.deleteKey) {
    return;
  }

  if (typeof cookieStore === "undefined") {
    return;
  }

  // クッキー値があったら引っ越し
  const cookieName = "user_delete_key";
  const cookieKey = await cookieStore.get(cookieName);
  if (getCurrentSettings?.().deleteKey) {
    return;
  }
  if (cookieKey?.value) {
    store.setDeleteKey(cookieKey.value);
    await cookieStore.delete(cookieName);
  } else {
    store.resetDeleteKey();
  }
}
